import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';

export interface UserProfile {
  id: number;
  userId: number;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string | null;
  bio: string | null;
  businessName: string | null;
  businessType: string | null;
  phone: string | null;
  website: string | null;
  worldviaLastSyncedAt?: string | null;
  pendingConflicts?: ProfileConflict[] | null;
  gemStatus?: string | null;
}

export interface ProfileConflict {
  field: string;
  fieldLabel: string;
  aiviaValue: string | null;
  worldviaValue: string | null;
  worldviaUpdatedAt?: string;
  aiviaUpdatedAt?: string;
}

interface ProfileResponse {
  profile: UserProfile | null;
}

interface ConflictsResponse {
  conflicts: ProfileConflict[];
}

interface ResolveConflictParams {
  field: string;
  choice: 'aivia' | 'worldvia';
}

interface SyncResult {
  success: boolean;
  conflicts: ProfileConflict[];
  synced: string[];
  error?: string;
}

/**
 * Fetches the current user's profile data including firstName and lastName.
 * Falls back to the auth user data if profile is not available.
 */
export function useUserProfile() {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery<ProfileResponse>({
    queryKey: ['/api/profile'],
    queryFn: async () => {
      const response = await fetch('/api/profile');
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 1,
  });

  const profile = data?.profile;

  // Derive display values with fallbacks
  const firstName = profile?.firstName || user?.displayName?.split(' ')[0] || '';
  const lastName = profile?.lastName || user?.displayName?.split(' ').slice(1).join(' ') || '';
  const displayName = profile?.displayName || user?.displayName || '';
  const email = profile?.email || user?.email || '';
  const gemStatus = profile?.gemStatus?.trim() || null;

  return {
    profile,
    firstName,
    lastName,
    displayName,
    email,
    gemStatus,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Fetches pending worldvia.pro sync conflicts for the current user.
 */
export function useProfileConflicts() {
  const { isAuthenticated } = useAuth();

  const { data, isLoading, error, refetch } = useQuery<ConflictsResponse>({
    queryKey: ['/api/profile/conflicts'],
    queryFn: async () => {
      const response = await fetch('/api/profile/conflicts');
      if (!response.ok) {
        throw new Error('Failed to fetch profile conflicts');
      }
      return response.json();
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds - check more frequently for conflicts
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  return {
    conflicts: data?.conflicts || [],
    hasConflicts: (data?.conflicts?.length || 0) > 0,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Mutation hook for resolving a single profile conflict.
 */
export function useResolveConflict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ field, choice }: ResolveConflictParams) => {
      const response = await fetch('/api/profile/conflicts/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field, choice }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to resolve conflict');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate both profile and conflicts queries
      queryClient.invalidateQueries({ queryKey: ['/api/profile'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/conflicts'] });
    },
  });
}

/**
 * Mutation hook for manually triggering worldvia.pro sync.
 */
export function useSyncProfile() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult>({
    mutationFn: async () => {
      const response = await fetch('/api/profile/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to sync profile');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate both profile and conflicts queries
      queryClient.invalidateQueries({ queryKey: ['/api/profile'] });
      queryClient.invalidateQueries({ queryKey: ['/api/profile/conflicts'] });
    },
  });
}
